import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useCreateRequestMutation, useGetGeoObjectsQuery } from '@shared/api';
import { SURVEY_TYPES } from '@entities/geodesy';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';
import {
  ChoiceCard,
  ChoiceGrid,
  ErrorText,
  FieldGrid,
  FieldLabel,
  Hint,
  Notice,
  Row,
  Stack,
  TextArea,
  useAction,
} from '@widgets/GeoSchemes';

/**
 * «Нет схемы — запросить у геодезии»: заявка `geodesy` от функции `pto`, сразу
 * поданная (`submit: true`) — она тут же во входящих ОСНОВА.Геодезист.
 */
export function RequestSchemeDialog({ onClose }: { onClose: () => void }) {
  const { data: objects } = useGetGeoObjectsQuery();
  const { activeProjectId, setActiveProjectId } = useActiveProjectContext();
  const [createRequest] = useCreateRequestMutation();
  const { runValue, busy, error } = useAction();

  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState(activeProjectId);
  const [siteId, setSiteId] = useState('');
  const [elementId, setElementId] = useState('');
  const [surveyType, setSurveyType] = useState('executive');
  const [dueDate, setDueDate] = useState('');
  const [comment, setComment] = useState('');
  const [createdId, setCreatedId] = useState<string | null>(null);

  useEffect(() => {
    if (activeProjectId && !projectId) {
      setProjectId(activeProjectId);
    }
  }, [activeProjectId, projectId]);

  const allElements = objects?.elements ?? [];
  const elements = allElements.filter(
    (el) =>
      (!siteId || el.site_id === siteId) &&
      (!projectId || !el.project_id || el.project_id === projectId),
  );

  function pickElement(id: string) {
    setElementId(id);
    const el = allElements.find((x) => x.id === id);
    if (el) {
      if (!siteId && el.site_id) setSiteId(el.site_id);
      if (!projectId && el.project_id) setProjectId(el.project_id);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    if (projectId) {
      const proj = (objects?.projects ?? []).find((p) => p.id === projectId);
      setActiveProjectId(projectId, { name: proj?.name });
    }
    const res = await runValue(
      () =>
        createRequest({
          request_type: 'geodesy',
          source_function: 'pto',
          title: title.trim(),
          ...(projectId ? { project_id: projectId } : {}),
          ...(siteId ? { site_id: siteId } : {}),
          ...(elementId ? { element_id: elementId } : {}),
          ...(dueDate ? { due_date: dueDate } : {}),
          payload: {
            survey_type: surveyType,
            ...(comment.trim() ? { comment: comment.trim() } : {}),
          },
          submit: true,
        }).unwrap(),
      'Заявка не отправлена.',
    );
    if (res) setCreatedId(res.request.id);
  }

  if (createdId) {
    return (
      <Modal open title="Заявка отправлена" onClose={onClose}>
        <Stack $gap={4}>
          <Notice $tone="success" role="status">
            Заявка уже во входящих ОСНОВА.Геодезист. Когда геодезист передаст схему, она появится в
            этом реестре со статусом «На проверке в ПТО».
          </Notice>
          <Row $gap={2}>
            <Link to={`/requests/${createdId}`}>Открыть заявку</Link>
            <Button type="button" $variant="secondary" onClick={onClose}>
              Закрыть
            </Button>
          </Row>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal open title="Запросить схему у геодезии" onClose={busy ? () => undefined : onClose}>
      <form onSubmit={(e) => void submit(e)}>
        <Stack $gap={4}>
          <Hint>Нет исполнительной схемы — оформите заявку, она сразу уйдёт геодезисту.</Hint>
          <FieldLabel>
            Что снять
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Исполнительная съёмка свайного поля, оси 1–8"
              autoFocus
              required
            />
          </FieldLabel>
          <Stack $gap={2}>
            <FieldLabel as="div">Вид съёмки</FieldLabel>
            <ChoiceGrid $min={140} role="radiogroup" aria-label="Вид съёмки">
              {SURVEY_TYPES.map((t) => (
                <ChoiceCard
                  key={t.value}
                  name="pto-geo-survey-type"
                  checked={surveyType === t.value}
                  onSelect={() => setSurveyType(t.value)}
                  title={t.label}
                />
              ))}
            </ChoiceGrid>
          </Stack>
          <FieldGrid $min={200}>
            <FieldLabel>
              Объект (проект ПТО)
              <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">— не выбран —</option>
                {(objects?.projects ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
            <FieldLabel>
              Площадка
              <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">— не выбрана —</option>
                {(objects?.sites ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
            <FieldLabel>
              Конструктивный элемент
              <Select value={elementId} onChange={(e) => pickElement(e.target.value)}>
                <option value="">— не выбран —</option>
                {elements.map((el) => (
                  <option key={el.id} value={el.id}>
                    {el.code ? `${el.code} — ${el.title}` : el.title}
                  </option>
                ))}
              </Select>
            </FieldLabel>
            <FieldLabel>
              Срок
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </FieldLabel>
          </FieldGrid>
          <FieldLabel>
            Комментарий
            <TextArea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Участок, отметки, что важно проверить"
            />
          </FieldLabel>
          {error && <ErrorText role="alert">{error}</ErrorText>}
          <Row $gap={2}>
            <Button type="submit" $variant="primary" disabled={busy || !title.trim()}>
              {busy ? 'Отправка…' : 'Отправить геодезисту'}
            </Button>
            <Button type="button" $variant="secondary" onClick={onClose} disabled={busy}>
              Отмена
            </Button>
          </Row>
        </Stack>
      </form>
    </Modal>
  );
}
